import React from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function ListingGuidelinesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Listing Guidelines</h1>
            <p className="text-muted-foreground">Standards for creating quality, trustworthy listings on FarmPack AI</p>
          </div>

          <Card className="border-green-200 bg-green-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-800">
                <CheckCircle className="h-5 w-5" />
                Required Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-green-700">
              <p>Every listing MUST include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Accurate Title:</strong> Clearly describe what you're selling (e.g., "Complete Catfish Farming Starter Package - 500 Fingerlings")
                </li>
                <li>
                  <strong>Detailed Description:</strong> Include specifications, conditions, yields, inclusions, and any limitations
                </li>
                <li>
                  <strong>Real Photos:</strong> Upload actual images of your products (minimum 1, recommended 3-5). If using stock photos, you MUST disclose this
                </li>
                <li>
                  <strong>Honest Pricing:</strong> State your actual selling price. No bait-and-switch or hidden fees
                </li>
                <li>
                  <strong>Location:</strong> Provide accurate location information for buyer awareness
                </li>
                <li>
                  <strong>Contact Information:</strong> Keep your phone number and WhatsApp details up to date
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <XCircle className="h-5 w-5" />
                Prohibited Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-red-700">
              <p>The following are STRICTLY PROHIBITED and will result in listing removal or account suspension:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Illegal Goods:</strong> Stolen items, endangered species, illegal livestock, prohibited chemicals
                </li>
                <li>
                  <strong>Fraudulent Listings:</strong> Items you don't have, bait-and-switch tactics, fake scarcity
                </li>
                <li>
                  <strong>Misleading Claims:</strong> Fake organic certification, exaggerated yields, false testimonials
                </li>
                <li>
                  <strong>Spam:</strong> Duplicate listings, irrelevant content, excessive reposting
                </li>
                <li>
                  <strong>Competing Platforms:</strong> Links to Jiji, Facebook Marketplace, or other platforms in your listing
                </li>
                <li>
                  <strong>Hate Speech/Discrimination:</strong> Any content promoting discrimination or harassment
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Package Listing Best Practices</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <h3 className="font-bold text-foreground mb-2">Creating Effective Package Listings:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Be Specific:</strong> List every item included (e.g., "500 catfish fingerlings, 50kg starter feed, 10m pond liner, water pump")
                </li>
                <li>
                  <strong>Include Quantities:</strong> Specify exact amounts, weights, or counts for all items
                </li>
                <li>
                  <strong>State Farming Type:</strong> Clearly indicate if it's poultry, catfish, goats, crops, etc.
                </li>
                <li>
                  <strong>Mention Expected Outcomes:</strong> Estimated yields, time to maturity, ROI estimates (if verifiable)
                </li>
                <li>
                  <strong>Add Photos:</strong> Show the actual package items, not just generic farm photos
                </li>
                <li>
                  <strong>Delivery Info:</strong> Clearly state if delivery is available and any associated costs
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Harvest Listing Best Practices</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <h3 className="font-bold text-foreground mb-2">Creating Effective Harvest Listings:</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Freshness:</strong> State harvest date and recommended consumption timeframe
                </li>
                <li>
                  <strong>Quantity Units:</strong> Use standard units (kg, bags, crates, pieces) and be consistent
                </li>
                <li>
                  <strong>Quality Grade:</strong> Be honest about quality (Grade A, B, or seconds)
                </li>
                <li>
                  <strong>Organic Certification:</strong> Only claim "organic" if you have valid certification
                </li>
                <li>
                  <strong>Availability Period:</strong> Set realistic expiry dates for perishable items
                </li>
                <li>
                  <strong>Storage Conditions:</strong> Mention if refrigeration or special handling is required
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-yellow-200 bg-yellow-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-800">
                <AlertTriangle className="h-5 w-5" />
                Pricing Guidelines
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-yellow-700">
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Fair Pricing:</strong> Research market rates before pricing. Extremely low prices may be flagged as suspicious
                </li>
                <li>
                  <strong>No Price Manipulation:</strong> Don't artificially inflate prices then offer "discounts"
                </li>
                <li>
                  <strong>Currency Accuracy:</strong> Use the correct currency for your region (GHS for Ghana, NGN for Nigeria)
                </li>
                <li>
                  <strong>Negotiable Indicator:</strong> Use the "Negotiable" option if you're open to price discussions
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Photo Guidelines</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <h3 className="font-bold text-foreground mb-2">Do's:</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Use clear, well-lit photos</li>
                <li>Show the actual items being sold</li>
                <li>Include multiple angles for packages</li>
                <li>Show scale/size references when relevant</li>
                <li>Disclose if photos are stock images</li>
              </ul>
              <h3 className="font-bold text-foreground mb-2 mt-4">Don'ts:</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>No blurry or dark photos</li>
                <li>No photos with watermarks from other platforms</li>
                <li>No misleading photos (showing premium items but selling lower grade)</li>
                <li>No inappropriate or offensive imagery</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Listing Status Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded-sm bg-yellow-100 text-yellow-700 text-[10px] font-bold uppercase">Draft</span>
                  <span>Save your listing but don't publish it yet. Only you can see drafts.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded-sm bg-blue-100 text-blue-700 text-[10px] font-bold uppercase">Pending Review</span>
                  <span>Your first 3 listings require admin approval. Subsequent listings may be auto-approved.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded-sm bg-green-100 text-green-700 text-[10px] font-bold uppercase">Active</span>
                  <span>Your listing is live and visible to buyers. Inquiries can be received.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded-sm bg-gray-100 text-gray-700 text-[10px] font-bold uppercase">Sold</span>
                  <span>Mark as sold when the item has been purchased. Listing remains visible but shows "Sold" status.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded-sm bg-red-100 text-red-700 text-[10px] font-bold uppercase">Expired</span>
                  <span>Listings auto-expire after 60 days. Renew to keep them active.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="px-2 py-0.5 rounded-sm bg-red-100 text-red-700 text-[10px] font-bold uppercase">Flagged</span>
                  <span>Admin has flagged your listing for review. You'll be notified of the issue.</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Off-Platform Transaction Safety</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p className="font-bold text-foreground">Remember: FarmPack AI is a listing platform only.</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Verify Buyers/Sellers:</strong> Check profiles, ask for references, verify identities before transacting
                </li>
                <li>
                  <strong>Inspect Before Payment:</strong> For physical goods, inspect before paying
                </li>
                <li>
                  <strong>Use Secure Payments:</strong> Mobile money, bank transfers with confirmation are recommended
                </li>
                <li>
                  <strong>Get Receipts:</strong> Always request proof of payment
                </li>
                <li>
                  <strong>Report Issues:</strong> Use the report button on suspicious listings or users
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Consequences of Violations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-muted-foreground">
              <p>Violations of these guidelines may result in:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Warning notification for first minor offense</li>
                <li>Listing removal for repeated or serious violations</li>
                <li>Temporary account suspension (3-7 days)</li>
                <li>Permanent account ban for severe or repeated violations</li>
                <li>Legal action for fraud or illegal activities</li>
              </ul>
              <p className="pt-4 border-t">
                Appeals can be submitted to support@farmpack-ai.com. We review all appeals fairly.
              </p>
            </CardContent>
          </Card>

          <div className="text-center pt-8 border-t">
            <p className="text-sm text-muted-foreground mb-4">
              By posting listings on FarmPack AI, you agree to follow these guidelines.
            </p>
            <Button onClick={() => navigate('/seller/dashboard')}>
              Go to Seller Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
